import { createReducer } from '@reduxjs/toolkit';
import { type AdditionalUserInfo } from '../../types';
import {
  authLogoutResponse,
  authLoginEmailResponse,
  authLoginGoogleResponse,
  authLoginTwitterResponse,
  authLoginFacebookResponse,
  authLoginWechatResponse,
  authSignupEmailResponse,
  authError,
  authSetIsProfileCreating,
  authSetIsAuthorized,
  authLoginGoogleRequest,
  authLoginTwitterRequest,
  authLoginFacebookRequest,
  authLoginWechatRequest,
  authLoginCustomTokenResponse,
} from './authActions';

interface AuthSlice {
  userId: string,
  authorized: boolean,
  isProfileCreating: boolean,
  additionalUserInfo?: AdditionalUserInfo,
  error?: string,
  onAuthRedirect?: string,
  isAdmin: boolean,
}

const initialState: AuthSlice = {
  userId: '',
  authorized: false,
  isProfileCreating: false,
  additionalUserInfo: undefined,
  error: undefined,
  onAuthRedirect: undefined,
  isAdmin: false,
};

const authReducer = createReducer(
  initialState,
  (builder) => builder
    .addCase(authLogoutResponse, (state) => {
      state.userId = '';
      state.error = undefined;
      state.authorized = false;
      state.additionalUserInfo = undefined;
      state.isAdmin = false;
    })
    .addCase(authLoginEmailResponse, (state, {
      payload: {
        additionalUserInfo, uid, isAdmin,
      },
    }) => {
      state.userId = uid;
      state.error = undefined;
      state.authorized = true;
      state.isProfileCreating = false;
      state.additionalUserInfo = additionalUserInfo;
      state.isAdmin = isAdmin;
    })
    .addCase(authLoginCustomTokenResponse, (state, {
      payload: {
        additionalUserInfo, uid, isAdmin,
      },
    }) => {
      state.userId = uid;
      state.error = undefined;
      state.authorized = true;
      state.isProfileCreating = false;
      state.additionalUserInfo = additionalUserInfo;
      state.isAdmin = isAdmin;
    })
    .addCase(authLoginGoogleRequest, (state) => {
      state.error = undefined;
    })
    .addCase(authLoginGoogleResponse, (state, {
      payload: {
        additionalUserInfo, uid, isAdmin,
      },
    }) => {
      state.userId = uid;
      state.error = undefined;
      state.additionalUserInfo = additionalUserInfo;
      state.isAdmin = isAdmin;
    })
    .addCase(authLoginTwitterRequest, (state) => {
      state.error = undefined;
    })
    .addCase(authLoginTwitterResponse, (state, {
      payload: {
        additionalUserInfo, uid, isAdmin,
      },
    }) => {
      state.userId = uid;
      state.error = undefined;
      state.additionalUserInfo = additionalUserInfo;
      state.isAdmin = isAdmin;
    })
    .addCase(authLoginFacebookRequest, (state) => {
      state.error = undefined;
    })
    .addCase(authLoginFacebookResponse, (state, {
      payload: {
        additionalUserInfo, uid, isAdmin,
      },
    }) => {
      state.userId = uid;
      state.error = undefined;
      state.additionalUserInfo = additionalUserInfo;
      state.isAdmin = isAdmin;
    })
    .addCase(authLoginWechatRequest, (state) => {
      state.error = undefined;
    })
    .addCase(authLoginWechatResponse, (state, {
      payload: {
        additionalUserInfo, uid, isAdmin,
      },
    }) => {
      state.userId = uid;
      state.error = undefined;
      state.additionalUserInfo = additionalUserInfo;
      state.isProfileCreating = true;
      state.isAdmin = isAdmin;
    })
    .addCase(authSignupEmailResponse, (state, {
      payload: {
        additionalUserInfo, uid, isAdmin,
      },
    }) => {
      state.userId = uid;
      state.error = undefined;
      state.authorized = true;
      state.isProfileCreating = false;
      state.additionalUserInfo = additionalUserInfo;
      state.isAdmin = isAdmin;
    })
    .addCase(authSetIsAuthorized, (state, { payload }) => {
      state.authorized = payload;
    })
    .addCase(authSetIsProfileCreating, (state, { payload }) => {
      state.isProfileCreating = payload;
    })
    .addCase(authError, (state, {
      payload: {
        error,
      },
    }) => {
      state.error = error.message;
    }),
);

export default authReducer;
